import { createContext, useContext, useMemo } from 'react';
import { uniqueId, find, sum, some, flow, reduce, reject, isNil } from 'lodash';

import { COLUMN_TYPES, COLUMN_TYPE_DEFAULTS, DEFAULT_COLUMN_CONFIG } from './DataTableColumns';
import { useTableColumnOverrides, useTableSorters } from './TableState';
import useIsPrinting from '../../utilities/PrintUtilities';
import { selectionRenderer } from './columns/Selection';

const MODE_UI = 'ui';
const MODE_PDF = 'pdf';
const MODE_CSV = 'csv';

const ColumnContext = createContext();
export function useColumns(showHidden) {
    const { columns } = useContext(ColumnContext);
    const overrides = useTableColumnOverrides();

    return useMemo(() => {
        return showHidden ? columns : reject(columns, (c) => find(overrides, { id: c.id })?.hidden);
    }, [showHidden, columns, overrides]);
}
export const useCsvColumns = () => useContext(ColumnContext).csvColumns;
export const useColumnGroups = () => useContext(ColumnContext).columnGroups;
export const useColumnContentWidth = () => useContext(ColumnContext).contentWidth;

export default function ColumnProvider(props) {
    const {
        children,
        config: initialColumns,
        showSelectionColumn,
        selectionColumnRenderCondition,
        viewportSize,
    } = props;

    const { width: viewportWidth } = viewportSize;

    const sorters = useTableSorters();
    const columnOverrides = useTableColumnOverrides();
    const isPrinting = useIsPrinting();

    const columnBuilderConfig = {
        columns: initialColumns,
        sorters,
        columnOverrides,
        showSelectionColumn,
        selectionColumnRenderCondition,
        isPrinting,
        viewportWidth,
    };

    const { columns, contentWidth } = useBuildColumns(columnBuilderConfig);
    const { columns: csvColumns } = useBuildColumns({ ...columnBuilderConfig, isCsvExport: true });

    const columnGroups = useMemo(() => buildColumnGroups(columns, columnOverrides), [columns, columnOverrides]);

    return (
        <ColumnContext.Provider value={{ columns, csvColumns, columnGroups, contentWidth }}>
            {children}
        </ColumnContext.Provider>
    );
}

/**
 * This hook builds out the columns which will be displayed in a datatable
 *
 * This controls sorting data and widths
 *
 * @param {Object[]} columns The user-configured columns
 * @param {Object[]} sorters The sorters on this table
 * @param {Number} tableWidth The width (in pixels) of the table
 */
function useBuildColumns(options) {
    const {
        columns: initialColumns,
        sorters,
        viewportWidth = 0,
        showSelectionColumn = false,
        selectionColumnRenderCondition,
        isCsvExport = false,
        isPrinting = false,
        columnOverrides,
    } = options;

    let mode = MODE_UI;

    if (isCsvExport) {
        mode = MODE_CSV;
    } else if (isPrinting) {
        mode = MODE_PDF;
    }

    // build columns, apply default configs, and assign IDs
    let columns = useMemo(
        () => buildColumns(initialColumns, showSelectionColumn, selectionColumnRenderCondition, columnOverrides, mode),
        [initialColumns, showSelectionColumn, selectionColumnRenderCondition, columnOverrides, mode]
    );

    // Apply width details when the table width changes
    columns = useMemo(
        () => assignWidths(columns, viewportWidth, columnOverrides),
        [columns, viewportWidth, columnOverrides]
    );

    // Apply sorting details to columns when they change
    columns = useMemo(() => assignSortConfig(columns, sorters), [columns, sorters]);

    const contentWidth = useMemo(
        () =>
            sum(
                columns.map((c) => {
                    const hiddenColIds = columnOverrides
                        .map((o) => (o.hidden ? o.id : null))
                        .filter((id) => id !== null);
                    if (hiddenColIds.includes(c.id)) {
                        return 0;
                    }

                    return c.width;
                })
            ),
        [columns, columnOverrides]
    );

    return { columns, contentWidth };
}

function buildColumnGroups(columns, columnOverrides) {
    // if none of the columns have a group configuration, bail. Most tables don't use this.
    if (!some(columns, (c) => !!c.group)) {
        return [];
    }

    return reduce(
        columns.filter((column) => {
            const columnOverride = columnOverrides.find((override) => override.id === column.id) || {};
            return !columnOverride.hidden;
        }),
        (groups, { width, group, id }) => {
            // group can be a string or translation
            const groupTitle = getGroupTitle(group);
            const [groupConfig] = groups.slice(-1);

            if (!groupConfig || groupTitle !== getGroupTitle(groupConfig.title)) {
                groups.push({
                    id, // the first column with this group. Used for child-keying, that's all.
                    title: group,
                    width,
                    hidden: !groupTitle,
                });
            } else {
                groupConfig.width += width;
            }

            return groups;
        },
        []
    );
}

function getGroupTitle(group) {
    return typeof group === 'object' ? group.id : group;
}

function buildColumns(initialColumns, showSelectionColumn, selectionColumnRenderCondition, columnOverrides, mode) {
    return flow([
        removeInvalidColumns,
        assignColumnIds,
        applyColumnDefaults,
        (cols) => applyColumnAssociations(cols, columnOverrides),
        (cols) => cols.filter((c) => isColumnVisible(c, mode)),
        (cols) => maybeAddFlexColumn(cols, columnOverrides),
        (cols) => maybeAddSelectionColumn(cols, showSelectionColumn, selectionColumnRenderCondition),
    ])(initialColumns);
}

function removeInvalidColumns(columns) {
    return reject(columns, isNil);
}

function isColumnVisible(column, mode) {
    const { csvOnly, uiOnly } = column;

    if (csvOnly === true && mode !== MODE_CSV) {
        return false;
    }

    if (uiOnly === true && mode !== MODE_UI) {
        return false;
    }

    return column?.hidden !== true;
}

function applyColumnAssociations(columns, columnOverrides) {
    // Find the columns with an assocId in the list of overrides, and if the column with assocId is hidden,
    // make sure that columnOverrides includes a hidden entry for each column associated with it. This is
    // necessary because any associated columns without a title will have calculated ids, not static ids.
    columns
        .filter((c) => c.assocId && find(columnOverrides, { id: c.assocId, hidden: true }))
        .forEach((c) => {
            if (!find(columnOverrides, { id: c.id })) {
                columnOverrides.push({ id: c.id, assocId: c.assocId, hidden: true });
            }
        });
    return columns;
}

function applyColumnDefaults(columns) {
    return columns.map((col) => {
        let column = { ...DEFAULT_COLUMN_CONFIG, ...col };
        column = { ...COLUMN_TYPE_DEFAULTS[column.type], ...col };

        if (column.width && column.minWidth) {
            column.minWidth = Math.min(column.width, column.minWidth);
        }

        return column;
    });
}

function maybeAddFlexColumn(columns, columnOverrides) {
    const hasFlex = !some(columns, (c) => {
        const columnOverride = getColumnOverride(c, columnOverrides);
        return c.flex && !columnOverride?.hidden;
    });
    if (hasFlex) {
        return [...columns, { ...COLUMN_TYPE_DEFAULTS[COLUMN_TYPES.FLEX], minWidth: 0 }];
    }

    return columns;
}

function maybeAddSelectionColumn(columns, showSelectionColumn, selectionColumnRenderCondition) {
    if (showSelectionColumn) {
        if (selectionColumnRenderCondition) {
            const selectionColumnWithRenderCondition = {
                ...COLUMN_TYPE_DEFAULTS[COLUMN_TYPES.SELECTION],
                renderer: (row) => (selectionColumnRenderCondition(row) ? selectionRenderer(row) : null),
            };
            return [selectionColumnWithRenderCondition, ...columns];
        }

        return [COLUMN_TYPE_DEFAULTS[COLUMN_TYPES.SELECTION], ...columns];
    }

    return columns;
}

function assignColumnIds(columns) {
    const colIds = [];
    return columns.map((col) => {
        let id = col.id || col.title?.message || col.title || uniqueId();

        if (find(colIds, (c) => id === c.id && col.mapping !== c.mapping)) {
            id += col.mapping;
        }

        colIds.push({ id, mapping: col.mapping });

        return { ...col, id };
    });
}

function assignSortConfig(columns, sorters) {
    return columns.map((col) => assignColumnSortConfig(col, sorters));
}

function assignColumnSortConfig(column, sorters) {
    const sortProps = {};

    if (column.sortable === true || (column.sortProperty && typeof column.sortProperty === 'string')) {
        sortProps.sortable = true;
        sortProps.isSorted = false;
        sortProps.sortProperty = column.sortProperty || column.mapping;

        const sorter = getColumnSorter(sortProps.sortProperty, sorters);

        if (sorter) {
            sortProps.isSorted = true;
            sortProps.sortDirection = sorter.direction === 'asc' ? 'asc' : 'desc';
        }
    }

    return { ...column, ...sortProps };
}

function getColumnSorter(sortProperty, sorters) {
    return find(sorters, { property: sortProperty });
}

function getColumnOverride(column, columnOverrides) {
    return find(columnOverrides, { id: column.id });
}

function assignWidths(columns, viewportWidth, columnOverrides) {
    const cols = columns.map(checkColumnOverrides);

    return cols.map((col) => processColumnWidth(col, cols, columnOverrides, viewportWidth)).filter((c) => c.width > 0);

    function checkColumnOverrides(column) {
        const columnOverride = getColumnOverride(column, columnOverrides);
        if (columnOverride?.width) {
            column.width = columnOverride.width;
            column.flex = 0;
        }
        return prepareColumnForWidth(column);
    }
}

function prepareColumnForWidth(column) {
    if (isFlexed(column)) {
        return {
            ...column,
            width: 0,
        };
    }

    return column;
}

function processColumnWidth(column, columns, columnOverrides, viewportWidth) {
    if (viewportWidth === 0) {
        return column;
    }

    // annoying, but for each column, we need to re-calculate each of these values
    // because constraints for minWidth can affect whether or not a column is flexed
    // or not
    const flexTotal = sum(
        columns.filter(isFlexed).map((c) => (getColumnOverride(c, columnOverrides)?.hidden ? 0 : c.flex))
    );
    const staticWidth = sum(
        columns.filter(isStaticWidth).map((c) => (getColumnOverride(c, columnOverrides)?.hidden ? 0 : c.width))
    );
    const availableWidth = viewportWidth - staticWidth;

    if (!isFlexed(column) && !isStaticWidth(column)) {
        throw new Error('Columns require a width or flex property for sizing!');
    }

    // skip any further processing since this isn't a flex column
    if (!isFlexed(column)) {
        return column;
    }

    const { flex, minWidth = 0 } = column;
    let width = Math.max(Math.floor(availableWidth * (flex / flexTotal)), 0);

    // return a no-longer flexed column, it is now fixed width since we can't satisfy
    // the minWidth flex constraint
    if (minWidth && width < minWidth) {
        width = minWidth;
    }

    return { ...column, flex: false, width };
}

function isFlexed(column) {
    return typeof column.flex === 'number' && column.flex > 0;
}

function isStaticWidth(column) {
    return typeof column.width === 'number' && column.width > 0;
}
